import { useEffect, useMemo, useState } from "react";
import { Shield, CheckCircle2, RefreshCcw, Search, Filter, Rocket, Table2, Plus } from "lucide-react";
import client, { errText } from "../lib/api";

/**
 * Content administrator console: panel sign items and promote them to live.
 *
 * A licensed reviewer signs draft items (one, or many at once) and the
 * service records the signature and moves each into PANEL_REVIEWED. Moving
 * an item all the way to LIVE still requires a signed norm table for its
 * construct at the target market and level (SI-14), surfaced inline on each
 * signed item, with a norm-table upload right here so a promotion can be
 * completed end to end.
 *
 * This surface never reads a person's answer or profile. Content
 * administrators touch items and norm tables only.
 */
export default function AdminItemsScreen() {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [norms, setNorms] = useState([]);
  const [filter, setFilter] = useState({ lifecycle_state: "draft", bank: "", construct: "" });
  const [reviewerId, setReviewerId] = useState(
    typeof window !== "undefined" ? localStorage.getItem("klareet.admin.reviewer") || "" : "",
  );
  const [signature, setSignature] = useState(
    typeof window !== "undefined" ? localStorage.getItem("klareet.admin.signature") || "" : "",
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [pendingId, setPendingId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [batchBusy, setBatchBusy] = useState(false);
  const [notice, setNotice] = useState("");

  const load = async () => {
    setBusy(true);
    setErr("");
    try {
      const [s, i, n] = await Promise.all([
        client.get("/content-admin/summary"),
        client.get("/content-admin/items", {
          params: {
            ...(filter.lifecycle_state ? { lifecycle_state: filter.lifecycle_state } : {}),
            ...(filter.bank ? { bank: filter.bank } : {}),
            ...(filter.construct ? { construct: filter.construct } : {}),
            limit: 400,
          },
        }),
        client.get("/content-admin/norm-tables"),
      ]);
      setSummary(s.data);
      setItems(i.data?.items || []);
      setNorms(n.data?.norm_tables || []);
      setSelected(new Set());
    } catch (e) {
      setErr(e?.response?.status === 403
        ? "You need a content administrator role to open this screen."
        : (errText(e, "Could not load the item bank.")));
      setItems([]);
      setSummary(null);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.lifecycle_state, filter.bank, filter.construct]);

  const rememberReviewer = (v) => {
    setReviewerId(v);
    if (typeof window !== "undefined") localStorage.setItem("klareet.admin.reviewer", v);
  };
  const rememberSignature = (v) => {
    setSignature(v);
    if (typeof window !== "undefined") localStorage.setItem("klareet.admin.signature", v);
  };
  const haveIdentity = reviewerId.trim() && signature.trim();

  const signItem = async (item) => {
    if (!haveIdentity) { setErr("Add your reviewer id and signature first."); return; }
    setPendingId(item.id);
    setErr("");
    try {
      await client.post(`/content-admin/items/${item.id}/panel-review`, {
        reviewer_id: reviewerId.trim(),
        signature: signature.trim(),
      });
      await load();
    } catch (e) {
      setErr(errText(e, "Sign failed for this item."));
    } finally {
      setPendingId(null);
    }
  };

  const search = (list, query) => {
    if (!query.trim()) return list;
    const s = query.trim().toLowerCase();
    return list.filter((it) =>
      (it.id || "").toLowerCase().includes(s) ||
      (it.stem_text || "").toLowerCase().includes(s) ||
      (it.construct || "").toLowerCase().includes(s),
    );
  };

  const visible = useMemo(() => search(items, q).slice(0, 200), [items, q]);
  const selectableVisible = visible.filter((it) => !it.panel_signed);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    const ids = selectableVisible.map((it) => it.id);
    const allOn = ids.length > 0 && ids.every((id) => selected.has(id));
    setSelected(allOn ? new Set() : new Set(ids));
  };

  const signSelected = async () => {
    if (!haveIdentity) { setErr("Add your reviewer id and signature first."); return; }
    const ids = Array.from(selected);
    if (!ids.length) return;
    setBatchBusy(true);
    setErr("");
    try {
      const { data } = await client.post("/content-admin/items/batch-panel-review", {
        item_ids: ids,
        reviewer_id: reviewerId.trim(),
        signature: signature.trim(),
      });
      if ((data?.failed_count || 0) > 0) {
        setErr(`${data.signed_count} signed, ${data.failed_count} could not be signed.`);
      }
      await load();
    } catch (e) {
      setErr(errText(e, "Batch sign failed."));
    } finally {
      setBatchBusy(false);
    }
  };

  const promote = async (id, market, level) => {
    try {
      await client.post(`/content-admin/items/${id}/promote-to-live`, { market, level });
      // The item leaves the panel-reviewed filter on reload, so confirm at
      // the screen level where the message survives the refresh.
      setNotice(`${id} is now live at ${market} / ${level}.`);
      setTimeout(() => setNotice(""), 5000);
      await load();
      return { ok: true, msg: "Promoted to live." };
    } catch (e) {
      return { ok: false, msg: errText(e, "Could not promote this item.") };
    }
  };

  const uploadNorm = async (payload) => {
    try {
      await client.post("/content-admin/norm-tables", payload);
      await load();
      return { ok: true, msg: "Norm table signed and active." };
    } catch (e) {
      return { ok: false, msg: errText(e, "Could not save that norm table.") };
    }
  };

  const constructsInView = useMemo(
    () => Array.from(new Set(items.map((it) => it.construct).filter(Boolean))).sort(),
    [items],
  );

  return (
    <div className="max-w-6xl mx-auto px-5 pt-8 pb-20">
      <div className="k-fade-in">
        <div className="k-label mb-2">content admin</div>
        <h1 className="klareet-serif mb-2" style={{ fontSize: "clamp(1.8rem, 4vw, 2.4rem)", lineHeight: 1.1 }} data-testid="admin-title">
          Panel signature console.
        </h1>
        <p className="k-hero-sub mb-6">
          Sign draft items one at a time or in a batch. Promote a signed item all the way to live once a signed norm table exists for its construct at the target market.
        </p>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="admin-summary">
          <SummaryTile label="draft" value={summary?.by_lifecycle?.draft || 0} />
          <SummaryTile label="panel reviewed" value={summary?.by_lifecycle?.panel_reviewed || 0} />
          <SummaryTile label="live" value={summary?.by_lifecycle?.live || 0} />
          <SummaryTile label="norm tables signed" value={`${summary?.norm_tables_signed || 0} / ${summary?.norm_tables_total || 0}`} />
        </div>
      )}

      <div className="k-card mb-6" data-testid="admin-reviewer-card">
        <div className="k-label mb-2">your reviewer identity</div>
        <p className="k-quiet mb-4 text-sm">
          These stay on this device only. Every signature is written to the audit trail with a masked value; the raw signature stays on the item record.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="k-label mb-1 block">reviewer id</label>
            <input className="k-input" placeholder="for example, panel-r-01" value={reviewerId} onChange={(e) => rememberReviewer(e.target.value)} data-testid="admin-reviewer-id" />
          </div>
          <div>
            <label className="k-label mb-1 block">signature</label>
            <input className="k-input" placeholder="a phrase only the reviewer knows" value={signature} onChange={(e) => rememberSignature(e.target.value)} data-testid="admin-signature" />
          </div>
        </div>
      </div>

      <NormTableCard
        norms={norms}
        constructs={constructsInView}
        signature={signature}
        onUpload={uploadNorm}
      />

      <div className="k-card mb-4" data-testid="admin-filters">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Filter size={14} style={{ color: "hsl(var(--klareet-muted))" }} />
            <select className="k-input !py-1.5 !px-3 text-sm" value={filter.lifecycle_state} onChange={(e) => setFilter((f) => ({ ...f, lifecycle_state: e.target.value }))} data-testid="admin-lifecycle-filter">
              <option value="">any lifecycle</option>
              <option value="draft">draft</option>
              <option value="drafted">drafted</option>
              <option value="panel_reviewed">panel reviewed</option>
              <option value="piloted">piloted</option>
              <option value="calibrated">calibrated</option>
              <option value="live">live</option>
              <option value="retired">retired</option>
            </select>
            <select className="k-input !py-1.5 !px-3 text-sm" value={filter.bank} onChange={(e) => setFilter((f) => ({ ...f, bank: e.target.value }))} data-testid="admin-bank-filter">
              <option value="">any bank</option>
              <option value="A">A narrative</option>
              <option value="B">B self report</option>
              <option value="C">C situational</option>
              <option value="D">D ability</option>
              <option value="E">E validity</option>
              <option value="DR">DR derailer</option>
            </select>
          </div>
          <div className="flex items-center gap-2 flex-1 min-w-[220px]">
            <Search size={14} style={{ color: "hsl(var(--klareet-muted))" }} />
            <input className="k-input !py-1.5 text-sm" placeholder="search item id, stem, or construct" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-search" />
          </div>
          <button onClick={load} className="k-btn k-btn-ghost !py-1.5 !px-3 text-sm" disabled={busy} data-testid="admin-refresh">
            <RefreshCcw size={12} /> {busy ? "loading" : "refresh"}
          </button>
        </div>
      </div>

      {notice && (
        <div
          className="k-card mb-4"
          style={{ borderColor: "hsl(var(--klareet-teal) / 0.5)", background: "hsl(var(--klareet-teal) / 0.06)" }}
          data-testid="admin-notice"
        >
          <div className="text-sm flex items-center gap-2" style={{ color: "hsl(var(--klareet-teal))" }}>
            <Rocket size={13} /> {notice}
          </div>
        </div>
      )}

      {err && (
        <div className="k-card mb-4" style={{ borderColor: "hsl(var(--destructive) / 0.5)" }} data-testid="admin-error">
          <div className="text-sm" style={{ color: "hsl(var(--destructive))" }}>{err}</div>
        </div>
      )}

      {/* Batch sign bar */}
      {selectableVisible.length > 0 && (
        <div
          className="k-card mb-4 flex flex-wrap items-center gap-3"
          style={{ background: "linear-gradient(180deg, hsl(var(--klareet-teal) / 0.06), hsl(var(--card)))" }}
          data-testid="admin-batch-signbar"
        >
          <label className="flex items-center gap-2 text-sm" style={{ color: "hsl(var(--klareet-ink))" }}>
            <input type="checkbox" checked={selectableVisible.length > 0 && selectableVisible.every((it) => selected.has(it.id))} onChange={toggleAll} data-testid="admin-select-all" />
            select all unsigned here ({selectableVisible.length})
          </label>
          <div className="k-quiet !text-xs" data-testid="admin-selected-count">{selected.size} selected</div>
          <button
            onClick={signSelected}
            disabled={batchBusy || selected.size === 0 || !haveIdentity}
            className="k-btn k-btn-primary !py-1.5 !px-3 text-sm ml-auto"
            data-testid="admin-batch-sign-btn"
          >
            <Shield size={12} /> {batchBusy ? "signing" : `sign selected (${selected.size})`}
          </button>
        </div>
      )}

      <div className="k-card overflow-hidden" data-testid="admin-items">
        <div className="k-label mb-3">items ({visible.length})</div>
        <div className="space-y-2">
          {visible.map((it) => {
            const signed = it.panel_signed;
            const isLive = it.lifecycle_state === "live";
            const testKey = `admin-item-${it.id}`;
            return (
              <div
                key={it.id}
                className="rounded-xl p-3 flex items-start gap-3"
                style={{
                  border: "1px solid hsl(var(--klareet-line-soft))",
                  background: signed
                    ? "linear-gradient(180deg, hsl(var(--klareet-teal) / 0.06), hsl(var(--card)))"
                    : "hsl(var(--card))",
                }}
                data-testid={testKey}
              >
                {!signed && (
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={selected.has(it.id)}
                    onChange={() => toggle(it.id)}
                    data-testid={`${testKey}-select`}
                    aria-label={`select ${it.id}`}
                  />
                )}
                <div className="klareet-mono text-xs shrink-0" style={{ color: "hsl(var(--klareet-muted))" }}>{it.id}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm" style={{ color: "hsl(var(--klareet-ink))" }}>{it.stem_text}</div>
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    <span className="k-chip !text-[10px]">bank {it.bank || "?"}</span>
                    <span className="k-chip !text-[10px]">{it.construct}</span>
                    <span className="k-chip !text-[10px]">{it.lifecycle_state}</span>
                    {it.minor_safe && <span className="k-chip k-chip-teal !text-[10px]">minor safe</span>}
                    {isLive && <span className="k-chip k-chip-teal !text-[10px]" data-testid={`${testKey}-live`}><Rocket size={10} /> live</span>}
                    {signed && !isLive && (
                      <span className="k-chip k-chip-teal !text-[10px]" data-testid={`${testKey}-signed`}><Shield size={10} /> signed</span>
                    )}
                  </div>
                  {signed && !isLive && (
                    <PromoteRow item={it} norms={norms} onPromote={promote} testKey={testKey} />
                  )}
                </div>
                <div className="shrink-0">
                  {isLive ? (
                    <div className="k-quiet !text-xs flex items-center gap-1"><Rocket size={12} style={{ color: "hsl(var(--klareet-teal))" }} /> live</div>
                  ) : signed ? (
                    <div className="k-quiet !text-xs flex items-center gap-1">
                      <CheckCircle2 size={12} style={{ color: "hsl(var(--klareet-teal))" }} /> by {it.panel_reviewer_id || "reviewer"}
                    </div>
                  ) : (
                    <button
                      onClick={() => signItem(it)}
                      disabled={pendingId === it.id || !haveIdentity}
                      className="k-btn k-btn-primary !py-1.5 !px-3 text-xs"
                      data-testid={`${testKey}-sign`}
                    >
                      <Shield size={11} /> {pendingId === it.id ? "signing" : "sign"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {visible.length === 0 && !busy && (
            <div className="k-quiet text-sm" data-testid="admin-empty">No items match this filter.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function PromoteRow({ item, norms, onPromote, testKey }) {
  const [open, setOpen] = useState(false);
  const [market, setMarket] = useState("IN");
  const [level, setLevel] = useState(item.reading_level || "adult");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const matchNorm = norms.find(
    (n) => n.construct === item.construct && n.market === market && n.level === level && n.active && n.panel_signed,
  );

  const doPromote = async () => {
    setBusy(true);
    setMsg(null);
    const r = await onPromote(item.id, market.trim(), level.trim());
    setMsg(r);
    setBusy(false);
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="mt-2 inline-flex items-center gap-1 text-xs k-quiet hover:opacity-80"
        data-testid={`${testKey}-promote-toggle`}
      >
        <Rocket size={11} /> promote to live
      </button>
    );
  }

  return (
    <div className="mt-3 rounded-lg p-3" style={{ background: "hsl(var(--klareet-canvas-2))", border: "1px solid hsl(var(--klareet-line-soft))" }} data-testid={`${testKey}-promote`}>
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="k-label !text-[10px] mb-1 block">market</label>
          <input className="k-input !py-1.5 !px-2 text-xs w-20" value={market} onChange={(e) => setMarket(e.target.value)} data-testid={`${testKey}-promote-market`} />
        </div>
        <div>
          <label className="k-label !text-[10px] mb-1 block">level</label>
          <input className="k-input !py-1.5 !px-2 text-xs w-28" value={level} onChange={(e) => setLevel(e.target.value)} data-testid={`${testKey}-promote-level`} />
        </div>
        <span
          className={`k-chip !text-[10px] ${matchNorm ? "k-chip-teal" : ""}`}
          title={`construct ${item.construct}`}
          data-testid={`${testKey}-promote-norm-state`}
        >
          {matchNorm ? "signed norm found" : "no signed norm yet"}
        </span>
        <button onClick={doPromote} disabled={busy} className="k-btn k-btn-primary !py-1.5 !px-3 text-xs" data-testid={`${testKey}-promote-btn`}>
          <Rocket size={11} /> {busy ? "promoting" : "promote"}
        </button>
      </div>
      {msg && (
        <div
          className="mt-2 text-xs"
          style={{ color: msg.ok ? "hsl(var(--klareet-teal))" : "hsl(var(--destructive))" }}
          data-testid={`${testKey}-promote-msg`}
        >
          {msg.msg}
        </div>
      )}
    </div>
  );
}

function NormTableCard({ norms, constructs, signature, onUpload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ market: "IN", level: "adult", construct: "", mean: "3.0", sd: "0.8", panel_signature: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (signature && !form.panel_signature) setForm((f) => ({ ...f, panel_signature: signature }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  const submit = async () => {
    if (!form.construct.trim() || !form.panel_signature.trim()) {
      setMsg({ ok: false, msg: "Construct and a panel signature are required." });
      return;
    }
    setBusy(true);
    setMsg(null);
    const r = await onUpload({
      market: form.market.trim(),
      level: form.level.trim(),
      construct: form.construct.trim(),
      mean: parseFloat(form.mean) || 0,
      sd: parseFloat(form.sd) || 0.0001,
      panel_signature: form.panel_signature.trim(),
    });
    setMsg(r);
    setBusy(false);
  };

  return (
    <div className="k-card mb-6" data-testid="admin-norm-card">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between" data-testid="admin-norm-toggle">
        <span className="k-label flex items-center gap-2"><Table2 size={14} /> norm tables</span>
        <span className="k-quiet !text-xs">{norms.length} on file · {open ? "hide" : "manage"}</span>
      </button>

      {open && (
        <div className="mt-4">
          <p className="k-quiet text-sm mb-3">
            A signed, active norm table for a construct at a market and level is what unlocks promotion to live for every item of that construct.
          </p>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="k-label !text-[10px] mb-1 block">market</label>
              <input className="k-input !py-1.5 text-sm" value={form.market} onChange={(e) => setForm((f) => ({ ...f, market: e.target.value }))} data-testid="admin-norm-market" />
            </div>
            <div>
              <label className="k-label !text-[10px] mb-1 block">level</label>
              <input className="k-input !py-1.5 text-sm" value={form.level} onChange={(e) => setForm((f) => ({ ...f, level: e.target.value }))} data-testid="admin-norm-level" />
            </div>
            <div>
              <label className="k-label !text-[10px] mb-1 block">construct</label>
              <input
                className="k-input !py-1.5 text-sm"
                list="admin-norm-constructs"
                placeholder="e.g. realistic"
                value={form.construct}
                onChange={(e) => setForm((f) => ({ ...f, construct: e.target.value }))}
                data-testid="admin-norm-construct"
              />
              <datalist id="admin-norm-constructs">
                {constructs.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div>
              <label className="k-label !text-[10px] mb-1 block">mean</label>
              <input className="k-input !py-1.5 text-sm" value={form.mean} onChange={(e) => setForm((f) => ({ ...f, mean: e.target.value }))} data-testid="admin-norm-mean" />
            </div>
            <div>
              <label className="k-label !text-[10px] mb-1 block">sd (positive)</label>
              <input className="k-input !py-1.5 text-sm" value={form.sd} onChange={(e) => setForm((f) => ({ ...f, sd: e.target.value }))} data-testid="admin-norm-sd" />
            </div>
            <div>
              <label className="k-label !text-[10px] mb-1 block">panel signature</label>
              <input className="k-input !py-1.5 text-sm" value={form.panel_signature} onChange={(e) => setForm((f) => ({ ...f, panel_signature: e.target.value }))} data-testid="admin-norm-signature" />
            </div>
          </div>
          <div className="flex items-center gap-3 mt-3">
            <button onClick={submit} disabled={busy} className="k-btn k-btn-primary !py-1.5 !px-3 text-sm" data-testid="admin-norm-upload-btn">
              <Plus size={12} /> {busy ? "saving" : "sign norm table"}
            </button>
            {msg && (
              <span className="text-xs" style={{ color: msg.ok ? "hsl(var(--klareet-teal))" : "hsl(var(--destructive))" }} data-testid="admin-norm-msg">
                {msg.msg}
              </span>
            )}
          </div>

          {norms.length > 0 && (
            <ul className="mt-4 space-y-1.5" data-testid="admin-norm-list">
              {norms.slice(0, 40).map((n) => (
                <li key={n.id} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`admin-norm-row-${n.id}`}>
                  <span className="k-chip !text-[10px]">{n.market}</span>
                  <span className="k-chip !text-[10px]">{n.level}</span>
                  <span className="k-chip !text-[10px]">{n.construct}</span>
                  <span className="k-quiet">mean {n.mean}, sd {n.sd}</span>
                  {n.panel_signed
                    ? <span className="k-chip k-chip-teal !text-[10px]"><Shield size={9} /> signed</span>
                    : <span className="k-chip !text-[10px]">unsigned</span>}
                  {!n.active && <span className="k-chip !text-[10px]">inactive</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function SummaryTile({ label, value }) {
  return (
    <div className="k-card" data-testid={`admin-summary-${label.replaceAll(" ", "-")}`}>
      <div className="k-label mb-1">{label}</div>
      <div className="klareet-mono" style={{ fontSize: "1.25rem", color: "hsl(var(--klareet-ink))" }}>{value}</div>
    </div>
  );
}
